// src/odoo/customers/pages/MultiCompaniesLicensesOverviewPage.tsx
// All comments are in English.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { fetchCustomerCompanies, type CustomerCompanyListItem } from "../api/customerListApi";
import { CustomerLicenseOverview } from "../components/CustomerLicenseOverview";

const STORAGE_KEY = "odoo_customer_multi_licenses_selected_companies";

const formatCompanyLabel = (company: CustomerCompanyListItem) =>
  company.company_name?.trim() || `Company #${company.company_partner_id}`;

const normalizeId = (value: unknown) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const loadSelectedCompanies = (): CustomerCompanyListItem[] => {
  if (typeof window === "undefined") return [];
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const deduped = new Map<number, CustomerCompanyListItem>();
    parsed
      .map((item) => {
        const id = normalizeId((item as any)?.company_partner_id);
        if (!id) return null;
        return {
          company_partner_id: id,
          company_name:
            typeof (item as any)?.company_name === "string" &&
            (item as any).company_name.trim()
              ? (item as any).company_name.trim()
              : `Company #${id}`,
        } as CustomerCompanyListItem;
      })
      .filter((item): item is CustomerCompanyListItem => Boolean(item))
      .forEach((item) => deduped.set(item.company_partner_id, item));
    return Array.from(deduped.values());
  } catch {
    return [];
  }
};

export default function MultiCompaniesLicensesOverviewPage() {
  const navigate = useNavigate();
  const [isCompanyPopoverOpen, setIsCompanyPopoverOpen] = useState(false);
  const [companySearchInput, setCompanySearchInput] = useState("");
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companies, setCompanies] = useState<CustomerCompanyListItem[]>([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<CustomerCompanyListItem[]>(
    () => loadSelectedCompanies()
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setCompanySearchTerm(companySearchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [companySearchInput]);

  useEffect(() => {
    let cancelled = false;

    const loadCompanies = async () => {
      setIsCompaniesLoading(true);
      setCompaniesError(null);
      try {
        const res = await fetchCustomerCompanies({
          q: companySearchTerm || undefined,
          page: 1,
          page_size: 200,
        });
        if (cancelled) return;
        setCompanies(res.items ?? []);
      } catch (error) {
        console.error("Failed to load companies", error);
        if (cancelled) return;
        setCompaniesError("Failed to load companies");
        setCompanies([]);
      } finally {
        if (!cancelled) setIsCompaniesLoading(false);
      }
    };

    loadCompanies();
    return () => {
      cancelled = true;
    };
  }, [companySearchTerm]);

  useEffect(() => {
    if (selectedCompanies.length) {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(selectedCompanies)
      );
      return;
    }
    window.sessionStorage.removeItem(STORAGE_KEY);
  }, [selectedCompanies]);

  const selectedCompanyIds = useMemo(
    () => selectedCompanies.map((item) => item.company_partner_id),
    [selectedCompanies]
  );

  const selectedCompanyNames = useMemo(
    () => selectedCompanies.map((item) => item.company_name).filter(Boolean),
    [selectedCompanies]
  );

  const selectedSet = useMemo(
    () => new Set(selectedCompanyIds),
    [selectedCompanyIds]
  );

  const displayedCompanies = useMemo(() => {
    const term = companySearchInput.trim().toLowerCase();
    const list = companies ?? [];
    if (!term) {
      return [...list].sort((a, b) => {
        const aSelected = selectedSet.has(a.company_partner_id);
        const bSelected = selectedSet.has(b.company_partner_id);
        if (aSelected !== bSelected) return aSelected ? -1 : 1;
        return formatCompanyLabel(a).localeCompare(formatCompanyLabel(b));
      });
    }
    return list
      .filter((item) => {
        const label = formatCompanyLabel(item).toLowerCase();
        return (
          label.includes(term) ||
          String(item.company_partner_id).includes(term)
        );
      })
      .sort((a, b) => {
        const aSelected = selectedSet.has(a.company_partner_id);
        const bSelected = selectedSet.has(b.company_partner_id);
        if (aSelected !== bSelected) return aSelected ? -1 : 1;
        return formatCompanyLabel(a).localeCompare(formatCompanyLabel(b));
      });
  }, [companySearchInput, companies, selectedSet]);

  const isSelectorDisabled =
    isCompaniesLoading || !!companiesError || companies.length === 0;

  const toggleCompanySelection = (company: CustomerCompanyListItem) => {
    setSelectedCompanies((prev) => {
      const exists = prev.some(
        (item) => item.company_partner_id === company.company_partner_id
      );
      if (exists) {
        return prev.filter(
          (item) => item.company_partner_id !== company.company_partner_id
        );
      }
      return [...prev, company];
    });
  };

  const removeCompanySelection = (companyPartnerId: number) => {
    setSelectedCompanies((prev) =>
      prev.filter((item) => item.company_partner_id !== companyPartnerId)
    );
  };

  const clearSelection = () => setSelectedCompanies([]);

  const selectedLabel =
    selectedCompanies.length === 0
      ? "Select companies"
      : selectedCompanies.length === 1
      ? selectedCompanies[0].company_name
      : `${selectedCompanies.length} companies selected`;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 border-b pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Multi Companies Active Licenses
          </h1>
          <p className="text-sm text-muted-foreground">
            Select one or more companies to view their license overview combined.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-6">
          <div className="flex min-w-[340px] flex-col">
            <label className="mb-1 text-xs font-medium text-muted-foreground">
              Companies
            </label>
            <Popover
              open={isCompanyPopoverOpen}
              onOpenChange={setIsCompanyPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isCompanyPopoverOpen}
                  className={cn(
                    "h-9 w-full justify-between text-sm",
                    isSelectorDisabled && "cursor-not-allowed opacity-70"
                  )}
                  disabled={isSelectorDisabled}
                >
                  {isCompaniesLoading
                    ? "Loading companies..."
                    : companiesError
                    ? "Failed to load companies"
                    : selectedLabel}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[340px] w-auto p-0" align="end">
                <Command>
                  <CommandInput
                    placeholder="Search company..."
                    className="h-9 text-xs"
                    value={companySearchInput}
                    onValueChange={setCompanySearchInput}
                  />
                  <CommandEmpty>No company found.</CommandEmpty>
                  <CommandGroup>
                    {displayedCompanies.map((company) => {
                      const isSelected = selectedSet.has(company.company_partner_id);
                      const label = formatCompanyLabel(company);
                      return (
                        <CommandItem
                          key={company.company_partner_id}
                          value={`${label} #${company.company_partner_id}`}
                          onSelect={() => toggleCompanySelection(company)}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={isSelected} />
                            <span className="truncate">{label}</span>
                          </div>
                          <div className="ml-2 flex items-center gap-2">
                            {isSelected ? (
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeCompanySelection(company.company_partner_id);
                                }}
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground"
                                aria-label={`Remove ${label} from selected companies`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ) : null}
                            <span className="text-[11px] text-muted-foreground">
                              #{company.company_partner_id}
                            </span>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </Command>
                <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    Select multiple companies and remove any selected company from the chips or list.
                  </span>
                  {selectedCompanies.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={clearSelection}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {selectedCompanies.length > 0 ? (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight">
              Selected companies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="text-muted-foreground">
              The overview below aggregates data across the selected companies.
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedCompanies.map((company) => {
                const label = formatCompanyLabel(company);
                return (
                  <span
                    key={company.company_partner_id}
                    className="inline-flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-xs text-foreground"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => removeCompanySelection(company.company_partner_id)}
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground"
                      aria-label={`Remove ${label} from selected companies`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 text-center">
          <p className="text-sm font-medium">No companies selected</p>
          <p className="max-w-md text-xs text-muted-foreground">
            Use the selector above to choose one or more companies and compare their license overview together.
          </p>
        </div>
      )}

      {selectedCompanies.length > 0 && (
        <CustomerLicenseOverview
          companyNames={selectedCompanyNames}
          companyPartnerIds={selectedCompanyIds}
        />
      )}
    </div>
  );
}
